/**
 * BillingPolicyCard — Client Profile → Billing section.
 *
 * Explicit, auditable billing policy per client:
 *   • billingMode selector: auto_charge | invoice_auto_send | invoice_manual
 *   • Card-on-file status (source of truth: lead.cardOnFileStatus)
 *   • AutoPay authorization — DISTINCT from card presence
 *   • Prepaid visits remaining (read-only informational)
 *
 * Backend contract: /api/leads/{leadId}/billing-policy — see
 * /app/backend/routes/billing_policy_routes.py. All mutations require
 * owner/admin (enforced server-side; UI hides the controls otherwise).
 *
 * AutoPay authorization mandates an explicit operator acknowledgment
 * ("I confirm that the customer authorized automatic charges after
 * completed services.") — this checkbox is REQUIRED and the endpoint
 * refuses submissions without it.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { CreditCard, Send, FileText, Zap, ShieldCheck, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useCompany } from '../../../../context/CompanyContext';
import { useRole } from '../../../../context/RoleContext';
import { auth } from '../../../../firebase';
import { Button } from '../../../../components/ui/button';

const MODES = [
  {
    id: 'auto_charge',
    label: 'AutoPay',
    desc: 'Charge the authorized card on file when a job is completed.',
    Icon: Zap,
    tone: 'emerald',
  },
  {
    id: 'invoice_auto_send',
    label: 'Invoice by SMS',
    desc: 'Send an invoice with payment link after each completed job. No auto-charge.',
    Icon: Send,
    tone: 'blue',
  },
  {
    id: 'invoice_manual',
    label: 'Manual Collection',
    desc: "Create an invoice, but do not send or charge it. Staff collects by check, cash, ACH, etc.",
    Icon: FileText,
    tone: 'slate',
  },
];

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = iso?.seconds ? new Date(iso.seconds * 1000) : new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return ''; }
};

const BillingPolicyCard = ({ client }) => {
  const { companyId } = useCompany();
  const { isAdmin } = useRole();

  const leadId = client?.leadId || (!client?.clientId ? client?.id : null);

  const [policy, setPolicy]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [authorizeOpen, setAuthorizeOpen] = useState(false);
  const [acknowledge, setAcknowledge]     = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const load = useCallback(async () => {
    if (!companyId || !leadId) return;
    setLoading(true);
    try {
      const token = await auth.currentUser?.getIdToken(false);
      const res = await fetch(
        `${backendUrl}/api/leads/${leadId}/billing-policy?companyId=${encodeURIComponent(companyId)}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPolicy(data);
    } catch (err) {
      console.error('[BillingPolicyCard] load error:', err);
      toast.error('Could not load billing policy');
    } finally {
      setLoading(false);
    }
  }, [companyId, leadId, backendUrl]);

  useEffect(() => { load(); }, [load]);

  const changeMode = async (newMode) => {
    if (!isAdmin || saving) return;

    // AutoPay is not a simple mode flip — it requires an explicit
    // authorization record. Route AutoPay clicks through the dedicated
    // authorization dialog instead of hitting the mode endpoint (which
    // now rejects unauthorized `auto_charge` with 409).
    if (newMode === 'auto_charge') {
      const authzValid = policy?.autoPayAuthorization?.status === 'authorized';
      if (!authzValid) {
        if (!policy?.hasActiveCard) {
          toast.error('AutoPay requires an active card on file. Ask the client to save a card first.');
          return;
        }
        // Open the authorization confirmation flow. Nothing is written
        // and no success toast fires until the operator acknowledges.
        setAuthorizeOpen(true);
        setAcknowledge(false);
        return;
      }
      // Already authorized → falling through will PUT auto_charge normally.
    }

    setSaving(true);
    try {
      const token = await auth.currentUser?.getIdToken(false);
      const res = await fetch(`${backendUrl}/api/leads/${leadId}/billing-mode`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ companyId, billingMode: newMode }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      const label = newMode.replace(/_/g, ' ');
      toast.success(`Billing mode set to ${label}`);
      await load();
    } catch (err) {
      toast.error(err.message || 'Could not update billing mode');
    } finally {
      setSaving(false);
    }
  };

  const authorizeAutoPay = async () => {
    if (!isAdmin || saving) return;
    if (!acknowledge) {
      toast.error('Please confirm the customer authorization first.');
      return;
    }
    setSaving(true);
    try {
      const token = await auth.currentUser?.getIdToken(false);
      const res = await fetch(`${backendUrl}/api/leads/${leadId}/autopay/authorize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          companyId,
          acknowledgment: true,
          source: 'operator',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      toast.success('AutoPay authorized — billing mode set to AutoPay');
      setAuthorizeOpen(false);
      setAcknowledge(false);
      await load();
    } catch (err) {
      // Do NOT show any AutoPay success toast on failure/cancel.
      toast.error(err.message || 'Could not authorize AutoPay');
      // Keep the dialog open so the operator can re-try or cancel.
    } finally {
      setSaving(false);
    }
  };

  const revokeAutoPay = async () => {
    if (!isAdmin || saving) return;
    if (!window.confirm('Revoke AutoPay authorization for this customer?')) return;
    setSaving(true);
    try {
      const token = await auth.currentUser?.getIdToken(false);
      const res = await fetch(`${backendUrl}/api/leads/${leadId}/autopay/revoke`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ companyId, reason: 'operator_action' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      toast.success('AutoPay revoked');
      await load();
    } catch (err) {
      toast.error(err.message || 'Could not revoke AutoPay');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-6 text-center text-sm text-gray-400" data-testid="billing-policy-loading">Loading billing policy…</div>;
  }
  if (!policy) return null;

  const currentMode = (policy.billingMode || policy.effectiveMode || 'invoice_auto_send');
  const effectiveMode = policy.effectiveMode || currentMode;
  const modeDemoted = currentMode !== effectiveMode;
  const isAutoPayActive = policy.autoPayAuthorization?.status === 'authorized';
  const isLegacy = policy.autoPayAuthorization?.status === 'legacy_unverified';
  const isRevoked = policy.autoPayAuthorization?.status === 'revoked';
  const cardBadge = policy.hasActiveCard
    ? `${policy.cardBrand || 'Card'} ••••${policy.cardLast4 || '****'}`
    : (policy.cardOnFileStatus === 'needs_update' ? 'Card needs update' : 'No card on file');

  return (
    <div className="space-y-4" data-testid="billing-policy-card">
      {/* ── Header row: card status + AutoPay pill ─────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${
            policy.hasActiveCard ? 'bg-green-50 text-green-700 border-green-200'
              : policy.cardOnFileStatus === 'needs_update'
              ? 'bg-amber-50 text-amber-700 border-amber-200'
              : 'bg-gray-100 text-gray-500 border-gray-200'
          }`}
          data-testid="billing-card-status"
        >
          <CreditCard className="w-3 h-3" />
          {cardBadge}
        </span>

        {isAutoPayActive && (
          <span
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200"
            data-testid="autopay-status-active"
            title={`Authorized ${fmtDate(policy.autoPayAuthorization?.authorizedAt)}${policy.autoPayAuthorization?.authorizedBy?.email ? ` by ${policy.autoPayAuthorization.authorizedBy.email}` : ''}`}
          >
            <ShieldCheck className="w-3 h-3" />
            AutoPay authorized
          </span>
        )}
        {isLegacy && (
          <span
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200"
            data-testid="autopay-status-legacy"
            title="Legacy record — does NOT authorize charging. Re-authorize below to enable AutoPay."
          >
            <AlertTriangle className="w-3 h-3" />
            Legacy record (not authorizing)
          </span>
        )}
        {isRevoked && (
          <span
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500 border border-gray-200"
            data-testid="autopay-status-revoked"
          >
            AutoPay revoked {fmtDate(policy.autoPayAuthorization?.revokedAt)}
          </span>
        )}
        {!isAutoPayActive && !isLegacy && !isRevoked && (
          <span
            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200"
            data-testid="autopay-status-none"
          >
            AutoPay not authorized
          </span>
        )}

        {policy.prepayPackage?.active && policy.prepayPackage?.remainingCoveredVisits > 0 && (
          <span
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-orange-50 text-orange-700 border border-orange-200"
            data-testid="prepay-remaining-badge"
          >
            {policy.prepayPackage.remainingCoveredVisits} prepaid visit{policy.prepayPackage.remainingCoveredVisits === 1 ? '' : 's'} remaining
          </span>
        )}

        <button
          type="button"
          onClick={load}
          className="ml-auto text-xs text-gray-400 hover:text-gray-600 inline-flex items-center gap-1"
          data-testid="billing-policy-refresh"
          title="Refresh"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* ── Mode selector ─────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {MODES.map(({ id, label, desc, Icon }) => {
          const active = currentMode === id;
          const disabled = !isAdmin || saving;
          return (
            <button
              key={id}
              type="button"
              disabled={disabled}
              onClick={() => changeMode(id)}
              data-testid={`billing-mode-${id}`}
              className={`text-left rounded-lg border p-3 transition-colors ${
                active
                  ? 'border-emerald-500 bg-emerald-50/50 ring-1 ring-emerald-500'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-4 h-4 ${active ? 'text-emerald-600' : 'text-gray-500'}`} />
                <span className="font-semibold text-sm text-gray-800">{label}</span>
                {active && (
                  <span className="ml-auto text-[10px] uppercase font-bold text-emerald-700 tracking-wide">Active</span>
                )}
              </div>
              <p className="text-xs text-gray-500 leading-snug">{desc}</p>
            </button>
          );
        })}
      </div>

      {/* Effective source callout — surface demotion prominently */}
      {modeDemoted && (
        <div
          className="text-xs rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-amber-900"
          data-testid="billing-mode-demoted"
        >
          <span className="font-semibold">Note:</span>{' '}
          AutoPay is selected but not authorized — completions will fall
          back to <span className="font-semibold">Invoice by SMS</span>{' '}
          until authorization is recorded.
        </div>
      )}
      {!modeDemoted && policy.effectiveSource && policy.effectiveSource !== 'lead_setting' && (
        <div
          className="text-xs rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-slate-600"
          data-testid="billing-effective-source"
        >
          <span className="font-semibold text-slate-700">Effective:</span>{' '}
          {policy.effectiveReason} <span className="text-slate-400">({policy.effectiveSource})</span>
        </div>
      )}

      {/* ── AutoPay authorization controls ────────────────────── */}
      {(currentMode === 'auto_charge' || authorizeOpen) && (
        <div className="rounded-lg border border-gray-200 p-3 bg-white space-y-2" data-testid="autopay-authz-block">
          {isAutoPayActive ? (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <span className="text-gray-700">
                Authorized {fmtDate(policy.autoPayAuthorization?.authorizedAt) || '—'}
                {policy.autoPayAuthorization?.authorizedBy?.email ? ` by ${policy.autoPayAuthorization.authorizedBy.email}` : ''}
                {policy.autoPayAuthorization?.paymentMethodLast4 ? ` on ••••${policy.autoPayAuthorization.paymentMethodLast4}` : ''}.
              </span>
              {isAdmin && (
                <Button
                  size="sm"
                  variant="outline"
                  className="ml-auto text-xs"
                  onClick={revokeAutoPay}
                  disabled={saving}
                  data-testid="autopay-revoke-btn"
                >
                  Revoke AutoPay
                </Button>
              )}
            </div>
          ) : isLegacy ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-amber-800">
                <AlertTriangle className="w-4 h-4" />
                <span>
                  A legacy AutoPay record exists but is <strong>not</strong> a
                  valid authorization — charging is blocked until the operator
                  records fresh consent.
                </span>
              </div>
              {isAdmin && !authorizeOpen && (
                <Button size="sm" onClick={() => setAuthorizeOpen(true)} data-testid="autopay-authorize-open-btn">
                  Confirm authorization
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-sm text-gray-600">
                {policy.hasActiveCard
                  ? 'Card is on file, but AutoPay has not been authorized.'
                  : 'AutoPay requires an active card on file.'}
              </div>
              {isAdmin && policy.hasActiveCard && !authorizeOpen && (
                <Button size="sm" onClick={() => setAuthorizeOpen(true)} data-testid="autopay-authorize-open-btn">
                  Authorize AutoPay
                </Button>
              )}
            </div>
          )}

          {authorizeOpen && isAdmin && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 space-y-2">
              <label className="flex items-start gap-2 text-sm text-amber-900">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={acknowledge}
                  onChange={(e) => setAcknowledge(e.target.checked)}
                  data-testid="autopay-ack-checkbox"
                />
                <span>
                  I confirm that the customer authorized automatic charges after completed services.
                </span>
              </label>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => { setAuthorizeOpen(false); setAcknowledge(false); }} data-testid="autopay-authorize-cancel-btn">
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={!acknowledge || saving}
                  onClick={authorizeAutoPay}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="autopay-authorize-submit-btn"
                >
                  Record authorization
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {!isAdmin && (
        <p className="text-xs text-gray-400" data-testid="billing-policy-readonly">
          Billing controls are read-only for your role.
        </p>
      )}
    </div>
  );
};

export default BillingPolicyCard;
